/**
 * TestOps Hub - Slim orchestrator
 *
 * Wires together four hooks and six presentational components.
 * All business logic lives in hooks; all rendering in components.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTestOpsCommandConfig } from './context'
import type { ToolCallRecord } from './external-types'
import type { PendingAction, ToolActivity, ConversationSummary, DetailPanelContent } from './types'
import { ConfirmModal } from './components/ConfirmModal'
import { exportConversationAsMarkdown } from './utils/exportMarkdown'

import { useChatReducer } from './hooks/useChatReducer'
import { useChatActions } from './hooks/useChatActions'
import { useChatEffects } from './hooks/useChatEffects'
import { useMyActions } from './hooks/useMyActions'
import { useExecutionPolling } from './hooks/useExecutionPolling'

import testOpsService from './api'
import { track, identify } from '@ytest/ui'
import * as Sentry from '@sentry/react'
import { HistorySidebar } from './components/HistorySidebar'
import { LandingView } from './components/LandingView'
import { ChatHeader } from './components/ChatHeader'
import { ChatMessageList } from './components/ChatMessageList'
import { ChatErrorBoundary } from './components/ChatErrorBoundary'
import { ChatInput } from './components/ChatInput'
import { MyActionsDrawer } from './components/MyActionsDrawer'
import { DetailPanel } from './components/DetailPanel'

// ---------------------------------------------------------------------------
// Greeting helper (pure) + live-refresh hook.
//
// Time tiers (browser-local hours):
//   00-04 → 🌕 Working late      (full moon — distinct from evening
//                                 crescent so the visual signals "deep
//                                 night" not just "after work")
//   05-11 → ☀️ Good morning
//   12-16 → ☀️ Good afternoon
//   17-23 → 🌙 Good evening      (crescent moon)
//
// Why a hook (not just useMemo): a tab kept open across a tier boundary
// would otherwise show a stale greeting until the user navigates or
// refreshes. The hook re-evaluates on a 60 s interval and on tab refocus.
// ---------------------------------------------------------------------------
function getGreeting(fullName?: string | null) {
  const hour = new Date().getHours()
  const name = fullName?.split(' ')[0] || 'there'
  if (hour < 5) {
    return { emoji: '\u{1F315}', timeOfDay: 'Working late', name }
  }
  if (hour < 12) {
    return { emoji: '\u{2600}\u{FE0F}', timeOfDay: 'Good morning', name }
  }
  if (hour < 17) {
    return { emoji: '\u{2600}\u{FE0F}', timeOfDay: 'Good afternoon', name }
  }
  return { emoji: '\u{1F319}', timeOfDay: 'Good evening', name }
}

function useGreeting(fullName?: string | null) {
  const [greeting, setGreeting] = useState(() => getGreeting(fullName))

  useEffect(() => {
    // Re-set immediately so the value reflects fullName changes too.
    setGreeting(getGreeting(fullName))

    // Poll once a minute. A boundary-aware setTimeout would be tighter
    // but adds complexity for no user-visible win — 60 s lag at the
    // boundary is invisible to humans.
    const intervalId = window.setInterval(() => {
      setGreeting(getGreeting(fullName))
    }, 60_000)

    // Refresh on tab refocus — common case is "tab open all day, user
    // comes back at 5 PM" where polling has already caught it but this
    // makes the transition feel instant.
    const onVisibilityChange = () => {
      if (!document.hidden) setGreeting(getGreeting(fullName))
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [fullName])

  return greeting
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function TestOpsHub() {
  // Auth is provided by the host via context — each ytest product has its
  // own auth store; the package stays unaware of the storage shape.
  const { user, tenant } = useTestOpsCommandConfig()
  const [searchParams, setSearchParams] = useSearchParams()
  const conversationIdFromUrl = searchParams.get('conversation')

  // ---- Reducer ----
  const [state, dispatchChat] = useChatReducer(conversationIdFromUrl)
  const {
    messages, input, isTyping, isRestoringConversation,
    hasStartedChat, selectedPortfolioId, activeConversationId,
    streamingAssistantContent,
  } = state

  // ---- Local UI state ----
  const [showIntegrations, setShowIntegrations] = useState(false)
  const [showPortfolioMenu, setShowPortfolioMenu] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [toolActivities, setToolActivities] = useState<ToolActivity[]>([])
  const [streamStatus, setStreamStatus] = useState<string | null>(null)
  const [expandedToolCalls, setExpandedToolCalls] = useState<Set<string>>(new Set())
  const [conversationPendingDelete, setConversationPendingDelete] = useState<ConversationSummary | null>(null)
  const [deletingConversationId, setDeletingConversationId] = useState<string | null>(null)
  const [attachedFile, setAttachedFile] = useState<File | null>(null)

  // Drawer animation state
  const [drawerRender, setDrawerRender] = useState(false)
  const [drawerVisible, setDrawerVisible] = useState(false)

  // Detail panel state — contextual slide-in for structured resource viewing
  const [detailPanelContent, setDetailPanelContent] = useState<DetailPanelContent | null>(null)
  const [detailPanelVisible, setDetailPanelVisible] = useState(false)
  // All panel resources in this conversation — for history switching
  const panelHistoryRef = useRef<Map<string, DetailPanelContent>>(new Map())

  // ---- Refs ----
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const streamAbortRef = useRef<AbortController | null>(null)
  const streamingContentMirror = useRef<string>('')
  const streamingToolCallsRef = useRef<ToolCallRecord[]>([])
  const createdInSessionRef = useRef<Set<string>>(new Set())
  const portfolioMenuRef = useRef<HTMLDivElement>(null)
  const integrationsButtonRef = useRef<HTMLDivElement>(null)

  // ---- Derived ----
  // Scope by user.id so drafts never leak across users sharing a browser tab.
  // sessionStorage outlives logout (auth-client only clears localStorage tokens),
  // so an unscoped key would surface User A's unsent text in User B's input.
  const draftStorageKey = user?.id
    ? (activeConversationId
        ? `testops-hub-draft:${user.id}:${activeConversationId}`
        : `testops-hub-draft:${user.id}:new`)
    : null

  const greeting = useGreeting(user?.full_name)

  // Tracks whether we've already consumed a `?prompt=` deep link this mount.
  // Declared up here with the other refs; the consuming effect lives below
  // `actions` so it can dispatch sendMessage directly.
  const prefillConsumedRef = useRef(false)

  // ---- Set user context for Sentry + Mixpanel ----
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email ?? undefined,
        username: user.full_name ?? undefined,
      })
      Sentry.setTag('tenant_id', tenant.id)
      identify(user.id, {
        $email: user.email,
        $name: user.full_name,
        tenant_id: tenant.id,
      })
    }
  }, [user, tenant])

  // ---- Effects hook (data loading, side effects) ----
  const effects = useChatEffects({
    conversationIdFromUrl,
    hasStartedChat,
    messages,
    input,
    toolActivities,
    streamStatus,
    streamingAssistantContent,
    showIntegrations,
    showPortfolioMenu,
    draftStorageKey,
    dispatchChat,
    streamAbortRef,
    createdInSessionRef,
    inputRef,
    messagesEndRef,
    setShowIntegrations,
    setShowPortfolioMenu,
    setPendingAction,
    setSearchParams,
    onPanelStateRestored: useCallback((panelState: Record<string, unknown>) => {
      // Restore panel history from DB.
      // DB stores panel events: {action, resourceKey, resourceType, title, provider, content}
      // Frontend uses: {resourceKey, resourceType, title, provider, data}
      panelHistoryRef.current.clear()
      const entries = Object.entries(panelState)
      for (const [key, raw] of entries) {
        const event = raw as Record<string, unknown>
        const panelContent: DetailPanelContent = {
          resourceKey: (event.resourceKey as string) || key,
          resourceType: (event.resourceType as DetailPanelContent['resourceType']) || 'generic',
          title: (event.title as string) || '',
          provider: (event.provider as string) || '',
          data: (event.content ?? event.data ?? null) as Record<string, unknown> | null,
        }
        panelHistoryRef.current.set(key, panelContent)
      }
      // Show the last resource
      if (entries.length > 0) {
        const lastKey = entries[entries.length - 1][0]
        const last = panelHistoryRef.current.get(lastKey)
        if (last) setDetailPanelContent(last)
      }
    }, []),
  })

  // ---- Actions hook ----
  const actions = useChatActions({
    messages,
    activeConversationId,
    selectedPortfolioId,
    draftStorageKey,
    userId: user?.id ?? null,
    inputRef,
    streamAbortRef,
    streamingContentMirror,
    streamingToolCallsRef,
    dispatchChat,
    setToolActivities,
    setStreamStatus,
    setPendingAction,
    setSearchParams,
    createdInSessionRef,
    loadConversationHistory: effects.loadConversationHistory,
    setConversationPendingDelete,
    deletingConversationId,
    setDeletingConversationId,
    conversationPendingDelete,
    conversationHistory: effects.conversationHistory,
    loadActivePins: effects.loadActivePins,
    input,
    attachedFile,
    setAttachedFile,
    setDetailPanelContent,
    panelHistoryRef,
  })

  // ---- My Actions hook ----
  const myActions = useMyActions(actions.sendMessage)

  // ---- Auto-send a deep-linked prompt (?prompt=…) ----
  // Used by dashboard surfaces (e.g. flaky-test rows) that want to start an
  // investigation in one click. We send the prompt straight to the AI rather
  // than just pre-filling the input — the dashboard's job is to start the
  // conversation, not to prepare a typing exercise.
  //
  // We strip the `prompt` param from the URL after consuming so a refresh
  // doesn't re-send. We also gate behind `isRestoringConversation` to avoid
  // racing with conversation history loading on initial mount.
  useEffect(() => {
    if (prefillConsumedRef.current) return
    if (isRestoringConversation) return
    const promptParam = searchParams.get('prompt')
    if (!promptParam) return
    prefillConsumedRef.current = true
    // Strip the param via window.history directly so we don't trigger React
    // Router's setSearchParams cascade, which would re-fire useChatEffects'
    // restoreConversation effect and dispatch `restore-empty` — wiping the
    // user message we're about to append.
    const url = new URL(window.location.href)
    url.searchParams.delete('prompt')
    window.history.replaceState(null, '', url.toString())
    void actions.sendMessage(promptParam)
  }, [searchParams, actions, isRestoringConversation])

  // ---- Persist panel state to localStorage when it changes ----
  useEffect(() => {
    if (!activeConversationId) return
    if (detailPanelContent) {
      panelHistoryRef.current.set(detailPanelContent.resourceKey, detailPanelContent)
      bumpPanelHistory()
      // Persist the full history map (capped at 20 entries client-side too)
      try {
        const entries = Array.from(panelHistoryRef.current.entries())
        const capped = entries.slice(-20)
        localStorage.setItem(
          `testops-panel:${activeConversationId}`,
          JSON.stringify(Object.fromEntries(capped)),
        )
      } catch { /* quota exceeded or serialization error — ignore */ }
    }
  }, [detailPanelContent, activeConversationId])

  // ---- Fallback: restore panel from localStorage if DB didn't provide it ----
  useEffect(() => {
    if (!activeConversationId || !hasStartedChat) return
    if (detailPanelContent) return // Already restored from DB or previous state
    try {
      const stored = localStorage.getItem(`testops-panel:${activeConversationId}`)
      if (stored) {
        const historyObj = JSON.parse(stored) as Record<string, DetailPanelContent>
        panelHistoryRef.current = new Map(Object.entries(historyObj))
        const entries = Object.values(historyObj)
        if (entries.length > 0) {
          setDetailPanelContent(entries[entries.length - 1])
        }
      }
    } catch { /* corrupted data — ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, hasStartedChat])

  // ---- Clear panel state on new chat ----
  useEffect(() => {
    if (!hasStartedChat) {
      panelHistoryRef.current.clear()
      setDetailPanelContent(null)
    }
  }, [hasStartedChat])

  // ---- Drawer animation + auto-fetch effect ----
  useEffect(() => {
    if (myActions.showMyActions) {
      setDrawerRender(true)
      myActions.loadActionItems()
      const raf = requestAnimationFrame(() => setDrawerVisible(true))
      return () => cancelAnimationFrame(raf)
    }
    if (drawerRender) {
      setDrawerVisible(false)
      const timeout = setTimeout(() => setDrawerRender(false), 200)
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [myActions.showMyActions, drawerRender])

  // ---- Escape key closes drawer ----
  useEffect(() => {
    if (!myActions.showMyActions) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') myActions.setShowMyActions(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [myActions.showMyActions, myActions.setShowMyActions])

  // ---- Detail panel animation ----
  useEffect(() => {
    if (detailPanelContent) {
      const raf = requestAnimationFrame(() => setDetailPanelVisible(true))
      return () => cancelAnimationFrame(raf)
    }
    setDetailPanelVisible(false)
    return undefined
  }, [detailPanelContent])

  const handleCloseDetailPanel = useCallback(() => {
    setDetailPanelVisible(false)
    track('TestOps Panel Closed', {
      resourceType: detailPanelContent?.resourceType,
      provider: detailPanelContent?.provider,
    })
    setTimeout(() => setDetailPanelContent(null), 200)
  }, [detailPanelContent])

  // ---- Switch to a previously viewed resource ----
  const handleSwitchResource = useCallback((resourceKey: string) => {
    const entry = panelHistoryRef.current.get(resourceKey)
    if (entry) {
      setDetailPanelContent(entry)
      track('TestOps Panel Tab Switch', { resourceType: entry.resourceType, provider: entry.provider })
    }
  }, [])

  // ---- Panel history as array for the strip ----
  // panelHistoryVersion increments whenever history ref is mutated,
  // ensuring the memo re-derives even without panel content change
  const [panelHistoryVersion, setPanelHistoryVersion] = useState(0)
  const bumpPanelHistory = useCallback(() => setPanelHistoryVersion(v => v + 1), [])

  // ---- Auto-poll running executions in the panel ----
  useExecutionPolling({
    panelContent: detailPanelContent,
    panelVisible: detailPanelVisible,
    onExecutionUpdate: useCallback((resourceKey: string, data: Record<string, unknown>, title: string) => {
      const updated: DetailPanelContent = {
        resourceKey,
        resourceType: 'execution',
        title,
        provider: 'testops',
        data,
      }
      panelHistoryRef.current.set(resourceKey, updated)
      bumpPanelHistory()
      setDetailPanelContent((prev) => {
        if (prev && prev.resourceKey === resourceKey) return updated
        return prev
      })
    }, [bumpPanelHistory]),
  })

  const panelHistory = useMemo(() => {
    return Array.from(panelHistoryRef.current.values())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailPanelContent, panelHistoryVersion])

  // ---- Toggle tool call expansion ----
  const handleToggleToolCall = useCallback((messageId: string, callId: string) => {
    const key = `${messageId}:${callId}`
    setExpandedToolCalls((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // ---- Pending action handlers ----
  const handleApprovePendingAction = useCallback((nonce: string) => {
    setPendingAction(null)
    void actions.sendMessage('Approved.', nonce)
  }, [actions])

  const handleCancelPendingAction = useCallback(() => {
    setPendingAction(null)
    void actions.sendMessage('Cancelled. Do not proceed with this operation.')
  }, [actions])

  const handleExportConversation = useCallback(async (conversationId: string) => {
    const summary = effects.conversationHistory.find((c) => c.id === conversationId)
    const title = summary?.title

    // If it's the active conversation, use in-memory messages
    if (conversationId === activeConversationId && messages.length > 0) {
      exportConversationAsMarkdown(messages, title)
      return
    }

    // Otherwise fetch from server
    try {
      const conversation = await testOpsService.getConversation(conversationId)
      const mapped = conversation.messages.map((m: any) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: new Date(m.created_at),
        toolCalls: m.tool_calls || undefined,
      }))
      exportConversationAsMarkdown(mapped, title || conversation.title)
    } catch (err) {
      console.error('Failed to export conversation:', err)
    }
  }, [messages, activeConversationId, effects.conversationHistory])

  const handleFeedback = useCallback((messageId: string, rating: 'up' | 'down' | null) => {
    dispatchChat({ type: 'set-message-feedback', payload: { messageId, feedback: rating } })
    testOpsService.submitFeedback(messageId, rating).catch((err) => {
      console.error('Failed to submit feedback:', err)
    })
  }, [])

  // ---- Loading state ----
  if (isRestoringConversation) {
    return (
      <div className="flex h-full items-center justify-center bg-foreground/[0.04]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent/30 border-t-purple-600" />
          <p className="text-sm text-muted-foreground">Restoring conversation...</p>
        </div>
      </div>
    )
  }

  // ---- Derived: hide sidebar when detail panel is open ----
  const showSidebar = !detailPanelContent

  // ---- Render ----
  return (
    <div className="flex h-full overflow-hidden bg-foreground/[0.04]">
      {/* Sidebar — hidden when detail panel is open */}
      {showSidebar && <HistorySidebar
          conversationHistory={effects.conversationHistory}
          activeConversationId={activeConversationId}
          isLoadingHistory={effects.isLoadingHistory}
          deletingConversationId={deletingConversationId}
          onOpenConversation={actions.handleOpenConversation}
          onDeleteConversation={actions.handleDeleteConversation}
          onRenameConversation={async (id, title) => {
            try {
              await testOpsService.renameConversation(id, title)
              effects.loadConversationHistory()
            } catch (err) {
              console.error('Failed to rename conversation:', err)
            }
          }}
          onExportConversation={handleExportConversation}
          onNewChat={actions.handleNewChat}
        />}

        {/* Main area */}
        {!hasStartedChat ? (
          <LandingView
            greeting={greeting}
            input={input}
            onInputChange={(v) => dispatchChat({ type: 'set-input', payload: v })}
            onKeyDown={actions.handleKeyDown}
            onSend={actions.handleSend}
            inputRef={inputRef}
            portfolios={effects.portfolios}
            selectedPortfolioId={selectedPortfolioId}
            showPortfolioMenu={showPortfolioMenu}
            onSelectPortfolio={(id) => dispatchChat({ type: 'set-selected-portfolio', payload: id })}
            onTogglePortfolioMenu={() => setShowPortfolioMenu((p) => !p)}
            portfolioMenuRef={portfolioMenuRef}
            onToggleMyActions={() => myActions.setShowMyActions((p) => !p)}
            actionItems={myActions.actionItems}
            integrations={effects.integrations}
            showIntegrations={showIntegrations}
            onToggleIntegrations={() => setShowIntegrations((p) => !p)}
            onRefreshIntegrations={effects.loadIntegrations}
            integrationsButtonRef={integrationsButtonRef}
            onSendMessage={actions.sendMessage}
            attachedFile={attachedFile}
            onAttachFile={setAttachedFile}
          />
        ) : (
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Header spans full width above chat + panel */}
            <ChatHeader
              selectedPortfolioId={selectedPortfolioId}
              portfolios={effects.portfolios}
              activePins={effects.activePins}
              clearingPinKey={effects.clearingPinKey}
              onClearPin={effects.handleClearPin}
              onToggleMyActions={() => myActions.setShowMyActions((p) => !p)}
              actionItems={myActions.actionItems}
              integrations={effects.integrations}
              showIntegrations={showIntegrations}
              onToggleIntegrations={() => setShowIntegrations((p) => !p)}
              onRefreshIntegrations={effects.loadIntegrations}
              integrationsButtonRef={integrationsButtonRef}
            />
            {/* Chat + Panel row */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
            <div className="flex min-w-0 flex-1 flex-col">
            <ChatErrorBoundary>
            <ChatMessageList
              messages={messages}
              isTyping={isTyping}
              streamingAssistantContent={streamingAssistantContent}
              streamStatus={streamStatus}
              toolActivities={toolActivities}
              pendingAction={pendingAction}
              expandedToolCalls={expandedToolCalls}
              onToggleToolCall={handleToggleToolCall}
              onApprovePendingAction={handleApprovePendingAction}
              onCancelPendingAction={handleCancelPendingAction}
              onRetry={actions.handleRetry}
              onFeedback={handleFeedback}
              messagesEndRef={messagesEndRef}
              userInitials={
                user?.full_name
                  ? user.full_name
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)
                  : undefined
              }
            />
          </ChatErrorBoundary>
          <ChatInput
            input={input}
            onInputChange={(v) => dispatchChat({ type: 'set-input', payload: v })}
            onKeyDown={actions.handleKeyDown}
            onSend={actions.handleSend}
            inputRef={inputRef}
            attachedFile={attachedFile}
            onAttachFile={setAttachedFile}
            isTyping={isTyping}
            onStopGenerating={actions.handleStopGenerating}
          />
            </div>
            {/* Detail Panel — inside the chat+panel row so header stays full width */}
            {detailPanelContent && (
              <DetailPanel
                content={detailPanelContent}
                visible={detailPanelVisible}
                onClose={handleCloseDetailPanel}
                history={panelHistory}
                onSwitchResource={handleSwitchResource}
              />
            )}
            </div>
          </div>
        )}

      {/* My Actions Drawer */}
      <MyActionsDrawer
        render={drawerRender}
        visible={drawerVisible}
        onClose={() => myActions.setShowMyActions(false)}
        actionItems={myActions.actionItems}
        loadingActions={myActions.loadingActions}
        onRefreshActions={myActions.loadActionItems}
        actionFilter={myActions.actionFilter}
        onSetActionFilter={myActions.setActionFilter}
        showCompletedActions={myActions.showCompletedActions}
        onToggleCompletedActions={() => myActions.setShowCompletedActions((p) => !p)}
        onExploreAction={myActions.handleActionExplore}
      />

      {/* Delete Conversation Confirmation */}
      <ConfirmModal
        isOpen={Boolean(conversationPendingDelete)}
        onClose={() => {
          if (!deletingConversationId) {
            setConversationPendingDelete(null)
          }
        }}
        onConfirm={actions.confirmDeleteConversation}
        isLoading={Boolean(deletingConversationId)}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${conversationPendingDelete?.title || 'this conversation'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  )
}
